import type { PickupVerificationStatus } from '@/types/pickupVerification';
import type {
  PickupComparisonView,
  PickupVerificationMismatch
} from './pickupReconciliationPolicy';

export const PICKUP_LOAN_CENTER_LABEL = 'Loan Center';
export const PICKUP_OPTIMAL_BLUE_LABEL = 'Optimal Blue';

export const PICKUP_COMPARISON_VIEW_OPTIONS: ReadonlyArray<{
  value: PickupComparisonView;
  label: string;
}> = [
  { value: 'all', label: 'All fields' },
  { value: 'blocking', label: 'Blocking' },
  { value: 'minor', label: 'Minor' }
];

export const PICKUP_VERIFICATION_LABELS: Record<PickupVerificationStatus, string> = {
  verified: 'Matches OB',
  verified_with_drift: 'Pricing inputs differ',
  pending_verification: 'Not checked yet',
  waiting_for_ob_sync: 'Not confirmed by OB',
  ob_mismatch: 'Lock mismatch',
  ob_access_error: 'OB access error'
};

export type PickupVerificationSeverity = 'major' | 'minor' | 'none' | 'not_checked' | 'checking';

export interface PickupVerificationShortPresentationInput {
  status?: PickupVerificationStatus | null;
  mismatchFields?: PickupVerificationMismatch[] | null;
  lastVerifiedAt?: string | null;
  canonicalStateChanged?: boolean;
  checking?: boolean;
}

const PICKUP_VERIFICATION_SHORT_LABELS: Record<PickupVerificationSeverity, string> = {
  major: 'Major',
  minor: 'Minor',
  none: 'None',
  not_checked: 'Not checked',
  checking: 'Checking…'
};

function formatPickupVerificationDate(value: string): string | null {
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) {
    return null;
  }
  return new Intl.DateTimeFormat('en-US', {
    month: 'short',
    day: 'numeric',
    timeZone: 'America/Los_Angeles'
  }).format(parsed);
}

export function pickupVerificationSeverity(
  input: PickupVerificationShortPresentationInput
): PickupVerificationSeverity {
  if (input.checking) {
    return 'checking';
  }
  if (!input.lastVerifiedAt) {
    return 'not_checked';
  }
  const hasBlockingFinding = (input.mismatchFields || [])
    .some(({ tier }) => tier === 'blocking');
  if (
    hasBlockingFinding
    || input.canonicalStateChanged
    || input.status === 'pending_verification'
    || input.status === 'waiting_for_ob_sync'
    || input.status === 'ob_mismatch'
    || input.status === 'ob_access_error'
  ) {
    return 'major';
  }
  if (
    input.status === 'verified_with_drift'
    || (input.mismatchFields || []).some(({ tier }) => tier === 'advisory')
  ) {
    return 'minor';
  }
  return 'none';
}

export function pickupVerificationShortLabel(
  input: PickupVerificationShortPresentationInput
): string {
  const severity = pickupVerificationSeverity(input);
  if (severity === 'checking' || severity === 'not_checked') {
    return PICKUP_VERIFICATION_SHORT_LABELS[severity];
  }
  const date = input.lastVerifiedAt
    ? formatPickupVerificationDate(input.lastVerifiedAt)
    : null;
  return date
    ? `${PICKUP_VERIFICATION_SHORT_LABELS[severity]} · ${date}`
    : PICKUP_VERIFICATION_SHORT_LABELS[severity];
}

export function pickupVerificationSeverityClass(
  severity: PickupVerificationSeverity
): string {
  if (severity === 'none') {
    return 'border-success/50 bg-success/5 text-success-strong';
  }
  if (severity === 'minor') {
    return 'border-warning/60 bg-warning/5 text-warning-strong';
  }
  if (severity === 'major') {
    return 'border-destructive/60 bg-destructive/5 text-destructive';
  }
  return 'border-muted-foreground/50 bg-muted text-muted-foreground';
}

export function pickupVerificationBadgeClass(status: PickupVerificationStatus): string {
  if (status === 'verified') {
    return 'border-success/50 bg-success/5 text-success-strong';
  }
  if (status === 'verified_with_drift') {
    return 'border-warning/60 bg-warning/5 text-warning-strong';
  }
  if (status === 'waiting_for_ob_sync' || status === 'ob_mismatch' || status === 'ob_access_error') {
    return 'border-destructive/60 bg-destructive/5 text-destructive';
  }
  return 'border-muted-foreground/50 bg-muted text-muted-foreground';
}

export function pickupVerificationLabel(
  status: PickupVerificationStatus | null | undefined
): string {
  return status ? PICKUP_VERIFICATION_LABELS[status] : 'Not checked yet';
}
